import { useEffect, useMemo, useState } from 'react';
import { contractsApi, type Contract, type ContractPayment } from '../lib/api/contracts';
import { formatVND } from '../lib/format';
import { attachmentsApi } from '../lib/api/attachments';

interface Props {
  open: boolean;
  contractId: string | null;
  onClose: () => void;
  onEdit?: (id: string) => void;
  /** Trigger reload trên page cha khi có thay đổi payment. */
  onChanged?: () => void;
  canWrite: boolean;
  canDelete: boolean;
}

const AVATAR_COLORS = ['#2563EB', '#dc2626', '#16a34a', '#f59e0b', '#7c3aed', '#0891b2', '#94a3b8', '#ea580c'];
function avatarColor(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const STATUS_META: Record<Contract['status'], { label: string; bg: string; fg: string; dot: string }> = {
  active: { label: 'Đang thực hiện', bg: '#dcfce7', fg: '#15803d', dot: '#22c55e' },
  completed: { label: 'Hoàn tất', bg: '#e0e7ff', fg: '#3730a3', dot: '#6366f1' },
  paused: { label: 'Tạm dừng', bg: '#f1f5f9', fg: '#475569', dot: '#94a3b8' },
  cancelled: { label: 'Đã huỷ', bg: '#fee2e2', fg: '#b91c1c', dot: '#dc2626' },
};

const PAY_STATUS_META: Record<ContractPayment['status'], { label: string; bg: string; fg: string }> = {
  paid: { label: 'Đã trả', bg: '#dcfce7', fg: '#15803d' },
  overdue: { label: 'Quá hạn', bg: '#fee2e2', fg: '#b91c1c' },
  pending: { label: 'Chưa đến hạn', bg: '#fef3c7', fg: '#a16207' },
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('vi-VN');
}

export function ContractDetailModal({ open, contractId, onClose, onEdit, onChanged, canWrite, canDelete }: Props) {
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Add payment form
  const [addOpen, setAddOpen] = useState(false);
  const [payDueDate, setPayDueDate] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [analyzingDocumentId, setAnalyzingDocumentId] = useState<string | null>(null);
  const [analysisMessage, setAnalysisMessage] = useState<string | null>(null);

  const reload = async () => {
    if (!contractId) return;
    setLoading(true); setErr(null);
    try {
      const c = await contractsApi.findOne(contractId);
      setContract(c);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !contractId) return;
    setContract(null); setAddOpen(false); setPayDueDate(''); setPayAmount('');
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contractId]);

  const paidTotal = useMemo(
    () => (contract?.payments ?? []).filter((p) => p.status === 'paid').reduce((s, p) => s + p.amount, 0),
    [contract],
  );
  const remaining = contract ? Math.max(0, contract.value - paidTotal) : 0;
  const paidPct = contract && contract.value > 0 ? Math.round((paidTotal / contract.value) * 100) : 0;

  if (!open) return null;

  const status = contract ? STATUS_META[contract.status] : null;
  const isExpense = contract?.kind === 'expense';
  const payVerb = isExpense ? 'chi trả' : 'thanh toán';

  const handleMarkPaid = async (paymentId: string) => {
    if (!window.confirm(`Đánh dấu kỳ này đã ${payVerb}?`)) return;
    try {
      await contractsApi.markPaid(paymentId);
      await reload();
      onChanged?.();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const handleRemovePayment = async (paymentId: string) => {
    if (!window.confirm('Xoá kỳ TT này?')) return;
    try {
      await contractsApi.removePayment(paymentId);
      await reload();
      onChanged?.();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const submitAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contract) return;
    const amt = Number(payAmount.replace(/\D/g, '')) || 0;
    if (amt < 1000) return alert('Số tiền tối thiểu 1.000đ.');
    if (!payDueDate) return alert('Chọn ngày đến hạn.');
    const nextIndex = (contract.payments?.length ?? 0) + 1;
    setSaving(true);
    try {
      await contractsApi.addPayment(contract.id, {
        periodIndex: nextIndex,
        dueDate: payDueDate,
        amount: amt,
      });
      setAddOpen(false); setPayDueDate(''); setPayAmount('');
      await reload();
      onChanged?.();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!contract) return;
    if (!window.confirm(`Xoá HĐ ${contract.code} - ${contract.name}?`)) return;
    setDeleting(true);
    try {
      await contractsApi.remove(contract.id);
      onChanged?.();
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setDeleting(false); }
  };

  const handleAnalyzeDocument = async (attachmentId: string) => {
    if (!contract) return;
    setAnalyzingDocumentId(attachmentId); setAnalysisMessage(null);
    try {
      const result = await contractsApi.analyzeDocument(contract.id, attachmentId);
      setAnalysisMessage(result.message);
    } catch (e) {
      setAnalysisMessage(e instanceof Error ? e.message : 'Không thể gửi tài liệu để phân tích.');
    } finally { setAnalyzingDocumentId(null); }
  };

  return (
    <div onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        {/* HERO HEADER */}
        <div style={heroHead}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: '#667085', fontWeight: 600 }}>{contract?.code ?? '…'}</span>
              {contract && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', background: isExpense ? '#fee2e2' : '#dcfce7', color: isExpense ? '#b91c1c' : '#15803d', fontSize: 11, fontWeight: 700, borderRadius: 9999 }}>
                  {isExpense ? 'HĐ CHI' : 'HĐ THU'}
                </span>
              )}
              {status && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', background: status.bg, color: status.fg, fontSize: 11, fontWeight: 600, borderRadius: 9999 }}>
                  <span style={{ width: 6, height: 6, background: status.dot, borderRadius: 9999, display: 'inline-block' }} />
                  {status.label}
                </span>
              )}
            </div>
            <button type="button" onClick={onClose} style={closeBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#172033', letterSpacing: -0.3 }}>
            {contract?.name ?? 'Đang tải…'}
          </h2>
          {contract && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: avatarColor(contract.customer), color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {initials(contract.customer)}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#172033' }}>{contract.customer}</div>
                  <div style={{ fontSize: 11, color: '#667085' }}>
                    {contract.customerTaxCode && `MST: ${contract.customerTaxCode}`}
                    {contract.customerTaxCode && contract.customerAddress && ' · '}
                    {contract.customerAddress}
                  </div>
                </div>
              </div>
              <Divider />
              <HeroStat label="Giá trị HĐ" value={formatVND(contract.value)} valueStyle={{ fontWeight: 800 }} />
              <Divider />
              <HeroStat
                label="Kỳ hạn"
                value={contract.startDate || contract.endDate
                  ? `${formatDate(contract.startDate)} → ${formatDate(contract.endDate)}`
                  : `Ký ${formatDate(contract.signedAt)}`}
              />
              {contract.salesperson && (<><Divider /><HeroStat label="Phụ trách" value={contract.salesperson} /></>)}
              {contract.latePenaltyRate != null && contract.latePenaltyRate > 0 && (
                <>
                  <Divider />
                  <HeroStat label="Lãi phạt trễ" value={`${contract.latePenaltyRate}%/tháng`} valueStyle={{ color: '#b91c1c' }} />
                </>
              )}
            </div>
          )}
        </div>

        {/* BODY */}
        <div style={body}>
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            {loading && <div style={{ color: '#64748b', fontSize: 13 }}>Đang tải…</div>}
            {err && <div style={errBox}>{err}</div>}

            {contract && (
              <>
                {/* Progress summary */}
                <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#334155' }}>Tiến độ {payVerb}</span>
                    <span style={{ fontSize: 11, color: '#667085' }}>
                      <b style={{ color: '#15803d' }}>{formatVND(paidTotal)}</b> / {formatVND(contract.value)}
                      <span style={{ margin: '0 8px', color: '#cbd5e1' }}>·</span>
                      Còn <b style={{ color: '#b91c1c' }}>{formatVND(remaining)}</b>
                    </span>
                  </div>
                  <div style={{ height: 8, background: '#f1f5f9', borderRadius: 9999, overflow: 'hidden' }}>
                    <div style={{ width: `${paidPct}%`, height: '100%', background: paidPct >= 100 ? '#3730a3' : '#22c55e' }} />
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{contract.periodsPaid}/{contract.periodsTotal} kỳ đã {payVerb}</span>
                    <span>{paidPct}%</span>
                  </div>
                </div>

                {/* Documents linked at contract creation / AI import */}
                <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '14px 18px' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#172033', marginBottom: 8 }}>Tài liệu hợp đồng</div>
                  {(contract.documents?.length ?? 0) === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>Chưa có tài liệu đính kèm.</div>
                  ) : contract.documents!.map((document) => (
                    <div key={document.id} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: '#f8fafc', borderRadius: 8, padding: '10px 12px', marginTop: 6 }}>
                      <span style={{ fontSize: 16 }}>📄</span><button type="button" onClick={() => attachmentsApi.openInNewTab(document.id, document.fileName)} style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#1e40af', fontWeight: 600, background: 'none', border: 0, cursor: 'pointer', padding: 0, textAlign: 'left' }}>{document.fileName}</button><span style={{ color: '#64748b', fontSize: 11 }}>{(document.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
                      <button type="button" onClick={() => handleAnalyzeDocument(document.id)} disabled={analyzingDocumentId === document.id} style={{ border: '1px solid #bfdbfe', color: '#2563eb', background: '#fff', borderRadius: 6, padding: '5px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>{analyzingDocumentId === document.id ? 'Đang gửi…' : '✦ AI phân tích lại'}</button>
                    </div>
                  ))}
                  {analysisMessage && <div style={{ marginTop: 10, padding: '9px 11px', borderRadius: 7, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e3a8a', fontSize: 12, lineHeight: 1.45 }}>{analysisMessage}</div>}
                </div>

                {/* Payment schedule */}
                <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '14px 18px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#172033' }}>Lịch {payVerb}</div>
                      <div style={{ fontSize: 11, color: '#667085', marginTop: 2 }}>Các kỳ {isExpense ? 'chi trả' : 'TT'} thực tế · click "Đã trả" để đánh dấu</div>
                    </div>
                    {canWrite && !addOpen && (
                      <button type="button" onClick={() => setAddOpen(true)} style={addPayBtn}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                        Thêm kỳ TT
                      </button>
                    )}
                  </div>

                  {addOpen && canWrite && (
                    <form onSubmit={submitAddPayment} style={{ marginBottom: 10, padding: 12, background: '#f8fafc', border: '1px solid #e4e7ec', borderRadius: 8, display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'end' }}>
                      <div>
                        <label style={smallLabel}>Ngày đến hạn</label>
                        <input type="date" value={payDueDate} onChange={(e) => setPayDueDate(e.target.value)} style={input} required />
                      </div>
                      <div>
                        <label style={smallLabel}>Số tiền</label>
                        <input
                          inputMode="numeric"
                          placeholder={formatVND(Math.round(remaining / Math.max(1, contract.periodsTotal - (contract.payments?.length ?? 0))))}
                          value={payAmount ? Number(payAmount.replace(/\D/g, '') || 0).toLocaleString('vi-VN') : ''}
                          onChange={(e) => setPayAmount(e.target.value)}
                          style={{ ...input, textAlign: 'right', fontWeight: 600 }}
                          required
                        />
                      </div>
                      <button type="submit" disabled={saving} style={saveBtnSm}>{saving ? '…' : 'Lưu'}</button>
                      <button type="button" onClick={() => setAddOpen(false)} style={cancelBtnSm}>Huỷ</button>
                    </form>
                  )}

                  {(contract.payments ?? []).length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                      Chưa có kỳ TT nào. {canWrite && 'Bấm "Thêm kỳ TT" để tạo lịch.'}
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead style={{ background: '#f8fafc' }}>
                        <tr style={{ textTransform: 'uppercase', color: '#64748b', fontSize: 11, letterSpacing: 0.3, height: 34, textAlign: 'left', borderBottom: '1px solid #eef2f7' }}>
                          <th style={{ padding: '0 12px', fontWeight: 600, width: 60 }}>Kỳ</th>
                          <th style={{ padding: '0 12px', fontWeight: 600, width: 130 }}>Hạn TT</th>
                          <th style={{ padding: '0 12px', fontWeight: 600, textAlign: 'right' }}>Số tiền</th>
                          <th style={{ padding: '0 12px', fontWeight: 600, width: 130 }}>Trạng thái</th>
                          <th style={{ padding: '0 12px', fontWeight: 600, width: 130 }}>Đã trả ngày</th>
                          {canWrite && <th style={{ padding: '0 12px', width: 130 }} />}
                        </tr>
                      </thead>
                      <tbody>
                        {(contract.payments ?? []).map((p) => {
                          const meta = PAY_STATUS_META[p.status];
                          return (
                            <tr key={p.id} style={{ borderTop: '1px solid #f1f5f9', height: 40 }}>
                              <td style={{ padding: '0 12px', fontWeight: 700, color: '#172033' }}>Kỳ {p.periodIndex}</td>
                              <td style={{ padding: '0 12px', color: '#334155' }}>{formatDate(p.dueDate)}</td>
                              <td style={{ padding: '0 12px', textAlign: 'right', color: '#172033', fontWeight: 600 }}>{formatVND(p.amount)}</td>
                              <td style={{ padding: '0 12px' }}>
                                <span style={{ padding: '2px 8px', background: meta.bg, color: meta.fg, fontSize: 11, fontWeight: 600, borderRadius: 9999 }}>{meta.label}</span>
                              </td>
                              <td style={{ padding: '0 12px', color: '#667085' }}>{p.paidAt ? formatDate(p.paidAt) : '—'}</td>
                              {canWrite && (
                                <td style={{ padding: '0 12px' }}>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    {p.status !== 'paid' && (
                                      <button type="button" onClick={() => handleMarkPaid(p.id)} style={miniPrimaryBtn}>✓ Đã trả</button>
                                    )}
                                    {p.status !== 'paid' && (
                                      <button type="button" onClick={() => handleRemovePayment(p.id)} title="Xoá kỳ" style={miniDangerBtn}>×</button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Thông tin bổ sung */}
                <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 10, padding: '14px 18px' }}>
                  <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#667085', fontWeight: 700, marginBottom: 10 }}>Thông tin bổ sung</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 8, columnGap: 16 }}>
                    <InfoRow label="Business Unit" value={contract.businessUnit} />
                    <InfoRow label="Loại dịch vụ" value={contract.serviceType} />
                    <InfoRow label="Ngày ký" value={formatDate(contract.signedAt)} />
                    <InfoRow label="Người liên hệ" value={contract.customerContact} />
                  </div>
                  {contract.notes && (
                    <>
                      <div style={{ marginTop: 12, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: '#667085', fontWeight: 700 }}>Ghi chú</div>
                      <div style={{ marginTop: 6, fontSize: 13, color: '#334155', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{contract.notes}</div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* FOOTER */}
        <div style={footer}>
          {canDelete && contract ? (
            <button type="button" onClick={handleDelete} disabled={deleting} style={deleteBtn}>
              {deleting ? 'Đang xoá…' : 'Xoá hợp đồng'}
            </button>
          ) : <div />}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Đóng</button>
            {canWrite && contract && (
              <button type="button" onClick={() => onEdit?.(contract.id)} style={editBtn}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" /><path d="m15 5 4 4" /></svg>
                Sửa HĐ
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 32, background: '#e4e7ec' }} />;
}

function HeroStat({ label, value, valueStyle }: { label: string; value: React.ReactNode; valueStyle?: React.CSSProperties }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#172033', ...valueStyle }}>{value}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  const display = value && value.trim() ? value : '—';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', padding: '4px 0' }}>
      <span style={{ fontSize: 12, color: '#64748b' }}>{label}</span>
      <span style={{ fontSize: 13, color: display === '—' ? '#94a3b8' : '#172033', fontWeight: display === '—' ? 400 : 500, textAlign: 'right' }}>{display}</span>
    </div>
  );
}

// ==== Styles ====

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
  zIndex: 60, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalBox: React.CSSProperties = {
  width: 920, maxWidth: '100%', height: 'calc(100vh - 48px)', maxHeight: 860,
  display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12,
  boxShadow: '0 25px 60px -12px rgba(15,23,42,.5)', overflow: 'hidden',
};
const heroHead: React.CSSProperties = {
  flex: '0 0 auto', padding: '20px 24px',
  background: 'linear-gradient(180deg,#f8fafc 0%,#fff 100%)',
  borderBottom: '1px solid #e4e7ec',
};
const closeBtn: React.CSSProperties = {
  width: 32, height: 32, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8,
  cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', overflowX: 'hidden', background: '#f8fafc', minHeight: 0 };
const footer: React.CSSProperties = {
  flex: '0 0 auto', padding: '14px 24px', borderTop: '1px solid #e4e7ec', background: '#fff',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const errBox: React.CSSProperties = {
  padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
  color: '#b91c1c', fontSize: 13,
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 16px', background: '#fff', border: '1px solid #e4e7ec', color: '#334155',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const editBtn: React.CSSProperties = {
  padding: '9px 20px', background: '#1d2939', color: '#fff', fontSize: 13, fontWeight: 600,
  border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const deleteBtn: React.CSSProperties = {
  padding: '9px 14px', background: '#fff', border: '1px solid #fca5a5', color: '#b91c1c',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const addPayBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 10px',
  background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe',
  fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
};
const smallLabel: React.CSSProperties = {
  display: 'block', fontSize: 11, color: '#64748b', fontWeight: 600, marginBottom: 4,
};
const input: React.CSSProperties = {
  width: '100%', height: 34, padding: '0 10px', border: '1px solid #e4e7ec',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};
const saveBtnSm: React.CSSProperties = {
  padding: '0 14px', height: 34, background: '#16a34a', color: '#fff', border: 'none',
  borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
};
const cancelBtnSm: React.CSSProperties = {
  padding: '0 12px', height: 34, background: '#fff', color: '#334155', border: '1px solid #e4e7ec',
  borderRadius: 6, fontSize: 12, cursor: 'pointer',
};
const miniPrimaryBtn: React.CSSProperties = {
  padding: '4px 10px', background: '#16a34a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: 12, fontWeight: 500, cursor: 'pointer',
};
const miniDangerBtn: React.CSSProperties = {
  padding: '4px 8px', background: '#fff', color: '#b91c1c', border: '1px solid #fca5a5',
  borderRadius: 4, fontSize: 14, fontWeight: 700, cursor: 'pointer', lineHeight: 1,
};
